import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const CareerManagement = () => {
  const [careers, setCareers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    job_type: '',
    description: '',
    requirements: '',
    responsibilities: '',
    is_internship: false,
    active: true,
  });

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const response = await api.get('/careers');
      setCareers(response.data);
    } catch (error) {
      toast.error('Failed to load careers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        requirements: formData.requirements.split('\n').filter(r => r.trim()),
        responsibilities: formData.responsibilities.split('\n').filter(r => r.trim()),
      };

      if (editingCareer) {
        await api.put(`/careers/${editingCareer.id}`, data);
        toast.success('Career updated');
      } else {
        await api.post('/careers', data);
        toast.success('Career created');
      }

      setShowModal(false);
      setEditingCareer(null);
      setFormData({ title: '', department: '', location: '', job_type: '', description: '', requirements: '', responsibilities: '', is_internship: false, active: true });
      fetchCareers();
    } catch (error) {
      toast.error('Failed to save career');
    }
  };

  const handleEdit = (career) => {
    setEditingCareer(career);
    setFormData({
      title: career.title,
      department: career.department,
      location: career.location,
      job_type: career.job_type,
      description: career.description,
      requirements: career.requirements.join('\n'),
      responsibilities: career.responsibilities.join('\n'),
      is_internship: career.is_internship,
      active: career.active,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.delete(`/careers/${id}`);
        toast.success('Career deleted');
        fetchCareers();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin"><ArrowLeft className="w-5 h-5" /></Link>
              <h1 className="text-2xl font-heading font-bold">Career Management</h1>
            </div>
            <Button onClick={() => setShowModal(true)} className="bg-primary" data-testid="create-career-button">
              <Plus className="w-4 h-4 mr-2" />New Career
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="space-y-4">
          {careers.map((career) => (
            <div key={career.id} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`career-card-${career.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2">{career.title}</h3>
                  <p className="text-slate-600 text-sm mb-2">{career.department} • {career.location} • {career.job_type}</p>
                  <p className="text-slate-600">{career.description}</p>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(career)} className="text-blue-600"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(career.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCareer ? 'Edit Career' : 'New Career'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Department *</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} required /></div>
              <div><Label>Location *</Label><Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required /></div>
              <div><Label>Job Type *</Label><Input value={formData.job_type} onChange={(e) => setFormData({...formData, job_type: e.target.value})} required /></div>
            </div>
            <div><Label>Description *</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required /></div>
            <div><Label>Requirements (one per line) *</Label><Textarea rows={4} value={formData.requirements} onChange={(e) => setFormData({...formData, requirements: e.target.value})} required /></div>
            <div><Label>Responsibilities (one per line) *</Label><Textarea rows={4} value={formData.responsibilities} onChange={(e) => setFormData({...formData, responsibilities: e.target.value})} required /></div>
            <div className="flex items-center space-x-2"><Switch checked={formData.is_internship} onCheckedChange={(checked) => setFormData({...formData, is_internship: checked})} /><Label>Is Internship</Label></div>
            <div className="flex items-center space-x-2"><Switch checked={formData.active} onCheckedChange={(checked) => setFormData({...formData, active: checked})} /><Label>Active</Label></div>
            <Button type="submit" className="w-full bg-primary">{editingCareer ? 'Update' : 'Create'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareerManagement;
